import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, Send, ScrollText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const KINDS = ["http", "s3", "datadog", "splunk_hec"];
const empty = { org_id: "", kind: "http", url: "", headers: {}, bucket: "", prefix: "", aws_creds_provider: "", enabled: true };

export default function AuditSinks() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [headersText, setHeadersText] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/audit-sinks"); setItems(data); }
    catch (err) { console.error("audit-sinks load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    let headers = {};
    if (headersText.trim()) {
      try { headers = JSON.parse(headersText); }
      catch { toast.error("Headers must be valid JSON"); return; }
    }
    const payload = { ...form, headers };
    if (form.kind !== "s3") { delete payload.bucket; delete payload.prefix; delete payload.aws_creds_provider; }
    if (form.kind === "s3") delete payload.url;
    try {
      await api.post("/audit-sinks", payload);
      toast.success("Sink created"); setOpen(false); setForm(empty); setHeadersText(""); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this audit sink?")) return;
    try { await api.delete(`/audit-sinks/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const test = async (id) => {
    try { await api.post(`/audit-sinks/${id}/test`); toast.success("Test event delivered"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout title="Audit Sinks" subtitle="LOG STREAMING">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="sinks-count">
          {items.length} SINKS · {items.filter((s) => s.enabled).length} ENABLED
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-sink-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><Plus className="w-4 h-4" /> NEW SINK</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xl">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ScrollText className="w-4 h-4 text-cyan-400" /> Create Audit Sink</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div><L>Org id</L><Input data-testid="sink-org-input" value={form.org_id} onChange={(e) => setForm({ ...form, org_id: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div><L>Kind</L>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="sink-kind-select" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {KINDS.map((k) => <SelectItem key={k} value={k} className="font-mono uppercase text-xs">{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.kind !== "s3" && (
                <div className="col-span-2"><L>URL</L><Input data-testid="sink-url-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              )}
              {form.kind === "s3" && (<>
                <div><L>Bucket</L><Input data-testid="sink-bucket-input" value={form.bucket} onChange={(e) => setForm({ ...form, bucket: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
                <div><L>Prefix</L><Input data-testid="sink-prefix-input" value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
                <div className="col-span-2"><L>AWS creds provider id</L><Input data-testid="sink-creds-input" value={form.aws_creds_provider} onChange={(e) => setForm({ ...form, aws_creds_provider: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              </>)}
              {form.kind !== "s3" && (
                <div className="col-span-2"><L>Headers (JSON)</L><Input data-testid="sink-headers-input" value={headersText} onChange={(e) => setHeadersText(e.target.value)} placeholder='{"Authorization": "Bearer ..."}' className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              )}
              <div className="col-span-2 flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-900">
                <span className="text-sm">Enabled</span>
                <Switch data-testid="sink-enabled-switch" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="submit-sink-btn" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">CREATE</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="sinks-list">
        {items.map((s) => (
          <div key={s.sink_id} data-testid={`sink-card-${s.sink_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-display text-base font-bold tracking-tight uppercase">{s.kind}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{s.sink_id} · org {s.org_id}</div>
              </div>
              <span className={"text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm " + (s.enabled ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-zinc-500 border-zinc-700")}>{s.enabled ? "ACTIVE" : "OFF"}</span>
            </div>
            <div className="font-mono text-[11px] text-zinc-400 break-all">{s.url || `s3://${s.bucket}/${s.prefix}`}</div>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800">
              <Button size="sm" variant="ghost" data-testid={`test-sink-${s.sink_id}`} onClick={() => test(s.sink_id)} className="gap-1.5"><Send className="w-3.5 h-3.5" />Test</Button>
              {user?.role === "admin" && (
                <Button size="sm" variant="ghost" data-testid={`delete-sink-${s.sink_id}`} onClick={() => del(s.sink_id)} className="gap-1.5 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no audit sinks configured.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
